import { Link, useLocation } from 'react-router-dom';
import { useRef, type FC, type ReactNode } from 'react';
import { PanelLeftOpen, type LucideIcon } from 'lucide-react';
import { Commentable } from '@/shared/comments/Commentable';
import { HelpAnchor } from '@/design-system/HelpAnchor';
import { cn } from '@/shared/cn';
import { APP_CHROME_CANVAS } from '@/shared/ui-class';
import { AccountMenu } from './AccountMenu';
import { SidebarEdgeToggle } from './SidebarEdgeToggle';
import { SidebarToggle } from './SidebarToggle';
import { useSidebarState } from '@/versions/v1/shared/SidebarStateContext';

export interface SidebarShellProps {
  children: ReactNode;
  header?: ReactNode;
  subheader?: ReactNode;
  footer?: ReactNode;
  widthPx?: number;
  ariaLabel?: string;
  className?: string;
  collapsedRail?: ReactNode;
  /** When `content`, children manage their own scroll regions (project chat). */
  scrollMode?: 'shell' | 'content';
}

export const SIDEBAR_RAIL_WIDTH_PX = 56;

export const SIDEBAR_ROW_BASE =
  'group mx-2 flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors';
export const SIDEBAR_ROW_INACTIVE =
  'text-zinc-700 hover:bg-zinc-900/5 dark:text-zinc-300 dark:hover:bg-white/5';
export const SIDEBAR_ACTIVE_ACCENT_BG =
  'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,transparent)]';
export const SIDEBAR_ROW_ACTIVE = `${SIDEBAR_ACTIVE_ACCENT_BG} font-medium text-zinc-900 dark:text-zinc-100`;
export const SIDEBAR_ROW_ICON_BASE =
  'h-4 w-4 flex-shrink-0 transition-colors';
export const SIDEBAR_ROW_ICON_INACTIVE =
  'text-zinc-500 group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-zinc-100';
export const SIDEBAR_ROW_ICON_ACTIVE =
  'text-accent-700 dark:text-accent-400';
export const SIDEBAR_SECTION_LABEL =
  'text-xs font-medium uppercase tracking-[0.12em] text-zinc-400 dark:text-zinc-500';

interface SidebarBodyProps {
  header?: ReactNode;
  subheader?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  collapseOnEmptyClick: React.MouseEventHandler<HTMLDivElement>;
  scrollMode?: 'shell' | 'content';
}

function SidebarBody({
  header,
  subheader,
  footer,
  children,
  collapseOnEmptyClick,
  scrollMode = 'shell',
}: SidebarBodyProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex h-14 flex-shrink-0 items-center justify-between gap-2 px-3">
        <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
          {header ?? <SidebarToggle variant="sidebar" />}
        </div>
        <HelpAnchor doc="workspace/sidebar" />
      </div>

      {subheader != null ? (
        <div className="flex-shrink-0">{subheader}</div>
      ) : null}

      <div className="relative min-h-0 flex-1">
        {scrollMode === 'content' ? (
          <div
            ref={scrollerRef}
            className="flex h-full min-h-0 flex-col"
            onClick={collapseOnEmptyClick}
            role="presentation"
          >
            {children}
          </div>
        ) : (
          <div
            ref={scrollerRef}
            className="scrollbar-subtle h-full overflow-y-auto"
            onClick={collapseOnEmptyClick}
            role="presentation"
          >
            {children}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 p-2">{footer ?? <AccountMenu />}</div>
    </div>
  );
}

export function SidebarShell({
  children,
  header,
  subheader,
  footer,
  widthPx = 260,
  ariaLabel,
  className,
  collapsedRail,
  scrollMode = 'shell',
}: SidebarShellProps) {
  const { open: sidebarOpen, toggle: toggleSidebar } = useSidebarState();
  const widthStyle = { width: `${widthPx}px` } as const;
  const railWidthStyle = { width: `${SIDEBAR_RAIL_WIDTH_PX}px` } as const;

  const collapseOnEmptyClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target === e.currentTarget) toggleSidebar();
  };

  const sidebarWidthPx = sidebarOpen
    ? widthPx
    : collapsedRail != null
      ? SIDEBAR_RAIL_WIDTH_PX
      : 0;
  const showEdgeToggle = collapsedRail != null;

  return (
    <Commentable name="SidebarShell">
      <div className="relative hidden h-full flex-shrink-0 md:flex">
        <aside
          aria-hidden={!sidebarOpen && collapsedRail == null}
          aria-label={ariaLabel}
          className={cn(
            'flex h-full flex-shrink-0 flex-col overflow-hidden transition-[width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
            'border-r border-black/[0.04] dark:border-white/[0.04]',
            APP_CHROME_CANVAS,
            className,
          )}
          style={
            sidebarOpen
              ? widthStyle
              : collapsedRail != null
                ? railWidthStyle
                : { width: 0 }
          }
        >
          {collapsedRail == null ? (
            <div
              className={cn(
                'flex h-full flex-shrink-0 flex-col overflow-hidden',
                'transition-[opacity,width] duration-200 ease-out',
                sidebarOpen
                  ? 'opacity-100'
                  : 'pointer-events-none opacity-0',
              )}
              style={sidebarOpen ? widthStyle : { width: 0 }}
            >
              <SidebarBody
                header={header}
                subheader={subheader}
                footer={footer}
                collapseOnEmptyClick={collapseOnEmptyClick}
                scrollMode={scrollMode}
              >
                {children}
              </SidebarBody>
            </div>
          ) : sidebarOpen ? (
            <div
              className={cn(
                'flex h-full flex-shrink-0 flex-col',
                'transition-opacity duration-200 ease-out',
                'opacity-100',
              )}
              style={widthStyle}
            >
              <SidebarBody
                header={header}
                subheader={subheader}
                footer={footer}
                collapseOnEmptyClick={collapseOnEmptyClick}
                scrollMode={scrollMode}
              >
                {children}
              </SidebarBody>
            </div>
          ) : (
            <div
              className="flex h-full flex-shrink-0 flex-col opacity-100 transition-opacity duration-200 ease-out"
              style={railWidthStyle}
            >
              {collapsedRail}
            </div>
          )}
        </aside>
        {showEdgeToggle ? (
          <SidebarEdgeToggle
            expanded={sidebarOpen}
            offsetPx={sidebarWidthPx}
            side="left"
            onToggle={toggleSidebar}
            expandLabel="Expand sidebar"
            collapseLabel="Collapse sidebar"
          />
        ) : null}
      </div>
    </Commentable>
  );
}

export interface SidebarSectionProps {
  label?: string;
  action?: ReactNode;
  first?: boolean;
  children: ReactNode;
}

export const SidebarSection: FC<SidebarSectionProps> = ({
  label,
  action,
  first,
  children,
}) => {
  return (
    <div>
      {label !== undefined || action !== undefined ? (
        <div
          className={cn(
            'mb-2 flex items-center justify-between px-3',
            first ? 'mt-4' : 'mt-6',
          )}
        >
          {label !== undefined ? (
            <span className={SIDEBAR_SECTION_LABEL}>{label}</span>
          ) : (
            <span />
          )}
          {action}
        </div>
      ) : null}
      {children}
    </div>
  );
};

export interface SidebarRowProps {
  href: string;
  icon: LucideIcon;
  label: string;
  isActive?: boolean;
  trailing?: ReactNode;
  disabled?: boolean;
  disabledHint?: string;
}

export const SidebarRow: FC<SidebarRowProps> = ({
  href,
  icon: Icon,
  label,
  isActive,
  trailing,
  disabled,
  disabledHint,
}) => {
  const { pathname } = useLocation();
  const active =
    isActive ??
    (pathname === href || pathname.startsWith(`${href}/`));

  const className = cn(
    SIDEBAR_ROW_BASE,
    active ? SIDEBAR_ROW_ACTIVE : SIDEBAR_ROW_INACTIVE,
    disabled && 'cursor-not-allowed opacity-40',
  );

  const content = (
    <>
      <Icon
        className={cn(
          SIDEBAR_ROW_ICON_BASE,
          active ? SIDEBAR_ROW_ICON_ACTIVE : SIDEBAR_ROW_ICON_INACTIVE,
        )}
        strokeWidth={1.6}
      />
      <span className="truncate leading-[1.35] tracking-[-0.003em]">
        {label}
      </span>
      {trailing != null ? <span className="ml-auto">{trailing}</span> : null}
    </>
  );

  if (disabled) {
    return (
      <span
        className={className}
        title={disabledHint ?? `${label} (soon)`}
        aria-label={disabledHint ?? label}
      >
        {content}
      </span>
    );
  }

  return (
    <Link
      to={href}
      aria-current={active ? 'page' : undefined}
      className={className}
      title={label}
    >
      {content}
    </Link>
  );
};

const SIDEBAR_RAIL_HIT_AREA =
  'flex h-6 w-6 items-center justify-center rounded-full p-0.5 transition-colors';
const SIDEBAR_RAIL_ICON = 'h-3.5 w-3.5 flex-shrink-0';
const SIDEBAR_RAIL_ICON_INACTIVE =
  'text-zinc-500 hover:bg-zinc-900/5 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-100';
const SIDEBAR_RAIL_ICON_ACTIVE = `${SIDEBAR_ACTIVE_ACCENT_BG} text-accent-700 dark:text-accent-400`;
const SIDEBAR_RAIL_ROW_SLOT = 'flex h-9 w-full items-center justify-center';

export interface SidebarRailButtonProps {
  href: string;
  icon: LucideIcon;
  label: string;
  isActive?: boolean;
}

export const SidebarRailButton: FC<SidebarRailButtonProps> = ({
  href,
  icon: Icon,
  label,
  isActive,
}) => {
  const { pathname } = useLocation();
  const active =
    isActive ??
    (pathname === href || pathname.startsWith(`${href}/`));

  return (
    <div className={SIDEBAR_RAIL_ROW_SLOT}>
      <Link
        to={href}
        aria-label={label}
        title={label}
        aria-current={active ? 'page' : undefined}
        className={cn(
          SIDEBAR_RAIL_HIT_AREA,
          active ? SIDEBAR_RAIL_ICON_ACTIVE : SIDEBAR_RAIL_ICON_INACTIVE,
        )}
      >
        <Icon className={SIDEBAR_RAIL_ICON} strokeWidth={1.6} />
      </Link>
    </div>
  );
};

export const SidebarRailOpenButton: FC = () => {
  const { toggle: toggleSidebar } = useSidebarState();

  return (
    <div className="flex h-14 flex-shrink-0 items-center justify-center">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggleSidebar();
        }}
        aria-label="Open sidebar"
        title="Open sidebar"
        className={cn(SIDEBAR_RAIL_HIT_AREA, SIDEBAR_RAIL_ICON_INACTIVE)}
      >
        <PanelLeftOpen className={SIDEBAR_RAIL_ICON} strokeWidth={1.6} />
      </button>
    </div>
  );
};

export const SidebarRail: FC<{ children: ReactNode }> = ({ children }) => {
  const { toggle: toggleSidebar } = useSidebarState();

  const expandOnEmptyClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!(e.target as HTMLElement).closest("a,button,[role='button']")) {
      toggleSidebar();
    }
  };

  return (
    <div
      className="flex h-full w-full cursor-pointer flex-col items-center"
      onClick={expandOnEmptyClick}
      role="presentation"
    >
      <SidebarRailOpenButton />
      <div className="h-11 w-full flex-shrink-0" aria-hidden />
      <div className="scrollbar-subtle flex w-full flex-1 flex-col items-center overflow-y-auto">
        <div
          className="flex h-10 w-full flex-shrink-0 items-center justify-center"
          aria-hidden
        >
          <div className="h-px w-6 bg-[var(--glass-border)]" />
        </div>
        {children}
      </div>
      <div className="flex flex-shrink-0 items-center justify-center py-2">
        <AccountMenu variant="compact" />
      </div>
    </div>
  );
};
